// RequestOtp — shown after a USER_PENDING login (account exists but the
// registration OTP was never verified). The phone is carried in via router
// state and shown read-only; the user chooses when to request the code, then
// moves on to the shared verify screen.

import { type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Button, Field, PhoneInput } from '@/components/common';
import { countries } from '@/mocks/countries';
import AuthLayout from '@/layout/AuthLayout';
import { OTPType, useSendOtpMutation } from '@/api/endpoints/authApi';
import type { ApiErrorShape } from '@/api/types';
import useOtpCooldown from '@/hooks/useOtpCooldown';
import { phoneOtpKey } from '@/redux/otp/otpKeys';
import AuthBack from './AuthBack';

interface RequestOtpState {
  otpType: OTPType;
  dialCode?: string;
  phone?: string;
}

const RequestOtp = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as RequestOtpState;

  const [sendOtp, { isLoading: submitting }] = useSendOtpMutation();

  const otpType = state.otpType;
  const dialCode = state.dialCode ?? '';
  const phone = state.phone ?? '';

  // A code sent moments ago (this screen or a resend) still cools this key —
  // block another request until it expires.
  const cooldown = useOtpCooldown(phone && otpType ? phoneOtpKey(otpType, dialCode, phone) : null);

  // The screen is meaningless without a phone from the previous step.
  if (!phone || !otpType) return <Navigate to='/login' replace />;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (cooldown.active) return;
    try {
      await sendOtp({ countryCode: dialCode, mobileNumber: phone, otpType }).unwrap();
      toast.success(t('toast_otp_sent'));
      navigate('/verify-otp', { state: { otpType, dialCode, phone } });
    } catch (err) {
      toast.error((err as ApiErrorShape)?.msg ?? t('error_generic'));
    }
  };

  return (
    <AuthLayout variant='login'>
      <AuthBack onClick={() => navigate('/login')} />
      <div className='auth-page'>
        <header className='auth-page__header'>
          <h1 className='auth-page__title'>{t('text_otp_verification')}</h1>
          <p className='auth-page__subtitle'>{t('text_request_otp_subtitle')}</p>
        </header>

        <form className='auth-page__form' onSubmit={handleSubmit} noValidate>
          <Field label={t('field_phone_number')} htmlFor='request-otp-phone'>
            <PhoneInput
              id='request-otp-phone'
              countries={countries}
              dialCode={dialCode}
              onDialCodeChange={() => {}}
              value={phone}
              onChange={() => {}}
              readOnly
            />
          </Field>

          <Button
            type='submit'
            size='lg'
            loading={submitting}
            disabled={cooldown.active}
            className='auth-page__submit'
          >
            {cooldown.active
              ? t('button_get_otp_in', { time: cooldown.label })
              : t('button_get_otp')}
          </Button>

          <p className='auth-page__alt'>
            <button type='button' className='auth-page__link' onClick={() => navigate('/login')}>
              {t('text_go_to_login')}
            </button>
          </p>
        </form>
      </div>
    </AuthLayout>
  );
};

export default RequestOtp;
